import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import PageHeader from "common/PageHeader";
import Footer from "common/Footer";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Delete,
  Settings as SettingsIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useSnackbar } from "../../context/SnackbarContext";
import { useCompany } from "../../context/CompanyContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openScanModal, setOpenScanModal] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanningCompanyId, setScanningCompanyId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newCompanyData, setNewCompanyData] = useState({
    name: "",
    company_code: "",
    inbox_email: "",
    invoice_allowed_extensions: ".pdf,.png,.jpg,.jpeg",
    invoice_sender_whitelist: "",
    invoice_email_pdf_split_enabled: false,
    description: "",
  });
  const { setSelectedCompany: setContextSelectedCompany, selectedCompany: contextSelectedCompany } =
    useCompany();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { t } = useLanguage();
  const theme = useTheme();

  const fetchCompanies = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/api/companies/`);
      if (response.data.companies) {
        setCompanies(response.data.companies);
      }
    } catch (error) {
      showSnackbar(t("companiesLoadFailed"), "error");
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSelectCompany = async (company) => {
    setContextSelectedCompany(company);
    showSnackbar(t("companySelected").replace("{name}", company.name), "success");
  };

  const handleDeleteCompany = async () => {
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/api/company/${selectedCompany.id}/delete/`);
      fetchCompanies();
      showSnackbar(t("companyDeleted"), "success");
      setOpenDeleteModal(false);
    } catch (error) {
      showSnackbar(t("companyDeleteFailed"), "error");
    }
  };

  const handleAddCompany = async () => {
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/api/company/create/`, newCompanyData);
      fetchCompanies();
      showSnackbar(t("companyAdded"), "success");
      setOpenAddModal(false);
      setNewCompanyData({
        name: "",
        company_code: "",
        inbox_email: "",
        invoice_allowed_extensions: ".pdf,.png,.jpg,.jpeg",
        invoice_sender_whitelist: "",
        invoice_email_pdf_split_enabled: false,
        description: "",
      });
    } catch (error) {
      showSnackbar(t("companyAddFailed"), "error");
    }
  };

  // Company settings (details, document receipt, recognition) live in Settings.
  const openCompanySettings = async (company) => {
    if (contextSelectedCompany?.id !== company.id) {
      await setContextSelectedCompany(company);
    }
    navigate("/settings?tab=company");
  };

  const handleOpenAddModal = () => {
    setNewCompanyData({
      name: "",
      company_code: "",
      inbox_email: "",
      invoice_allowed_extensions: ".pdf,.png,.jpg,.jpeg",
      invoice_sender_whitelist: "",
      description: "",
    });
    setOpenAddModal(true);
  };

  const emailStatusLabel = (status) =>
    ({
      imported: t("emailStatusImported"),
      pending_split: t("emailStatusPendingSplit"),
      skipped: t("emailStatusSkipped"),
      failed: t("emailStatusFailed"),
      already_processed: t("emailStatusAlreadyProcessed"),
    }[status] ||
    status ||
    "-");

  const documentStatusLabel = (status) =>
    ({
      present: t("documentStatusPresent"),
      partial: t("documentStatusPartial"),
      missing: t("documentStatusMissing"),
      unknown: t("documentStatusUnknown"),
      none: t("documentStatusNone"),
    }[status] ||
    status ||
    "-");

  const handleScanInbox = async (company) => {
    setScanningCompanyId(company.id);
    setScanResult(null);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/api/company/${company.id}/scan-invoice-email/`,
        { limit: 100 }
      );
      setScanResult(response.data);
      setOpenScanModal(true);
      showSnackbar(t("invoiceEmailScanCompleted"), "success");
      fetchCompanies();
    } catch (error) {
      const message = error.response?.data?.error || t("invoiceEmailScanFailed");
      showSnackbar(message, "error");
    } finally {
      setScanningCompanyId(null);
    }
  };

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
              }}
            >
              <PageHeader
                title={t("companyManagement")}
                actions={
                  <MDButton
                    variant="contained"
                    color="info"
                    onClick={handleOpenAddModal}
                    startIcon={<AddIcon />}
                  >
                    {t("addCompany")}
                  </MDButton>
                }
              />

              <MDBox p={{ xs: 2, md: 3 }}>
                <Box sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: theme.palette.action.hover }}>
                        <th
                          style={{
                            padding: "10px 12px",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {t("companyName")}
                        </th>
                        <th
                          style={{
                            padding: "10px 12px",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {t("companyCode")}
                        </th>
                        <th
                          style={{
                            padding: "10px 12px",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {t("invoiceEmail")}
                        </th>
                        <th
                          style={{
                            padding: "10px 12px",
                            textAlign: "left",
                            fontSize: "0.75rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {t("description")}
                        </th>
                        <th
                          style={{
                            padding: "10px 12px",
                            textAlign: "center",
                            fontSize: "0.75rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {t("status")}
                        </th>
                        <th
                          style={{
                            padding: "10px 12px",
                            textAlign: "center",
                            fontSize: "0.75rem",
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {t("actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map((company) => (
                        <tr key={company.id}>
                          <td
                            style={{
                              padding: "10px 12px",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <BusinessIcon color="info" />
                              <Typography variant="body2">{company.name}</Typography>
                            </Box>
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant="body2">{company.company_code}</Typography>
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {company.inbox_email || "-"}
                            </Typography>
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {company.description || "-"}
                            </Typography>
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              textAlign: "center",
                            }}
                          >
                            {contextSelectedCompany?.id === company.id ? (
                              <Chip
                                icon={<CheckCircleIcon />}
                                label={t("selected")}
                                color="success"
                                size="small"
                                sx={{ minWidth: 100 }}
                              />
                            ) : (
                              <Chip
                                label={t("notSelected")}
                                color="default"
                                size="small"
                                sx={{ minWidth: 100 }}
                              />
                            )}
                          </td>
                          <td
                            style={{
                              padding: "10px 12px",
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              textAlign: "center",
                            }}
                          >
                            <Box display="flex" justifyContent="center" gap={1}>
                              {contextSelectedCompany?.id !== company.id && (
                                <MDButton
                                  onClick={() => handleSelectCompany(company)}
                                  variant="contained"
                                  color="info"
                                  size="small"
                                >
                                  {t("select")}
                                </MDButton>
                              )}
                              <Tooltip title={t("scanInvoiceEmail")}>
                                <span>
                                  <IconButton
                                    onClick={() => handleScanInbox(company)}
                                    color="info"
                                    size="small"
                                    disabled={scanningCompanyId === company.id}
                                  >
                                    <EmailIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={t("settings")}>
                                <IconButton
                                  onClick={() => openCompanySettings(company)}
                                  color="info"
                                  size="small"
                                >
                                  <SettingsIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("deleteCompany")}>
                                <IconButton
                                  onClick={() => {
                                    setSelectedCompany(company);
                                    setOpenDeleteModal(true);
                                  }}
                                  color="error"
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Add Company Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("addNewCompany")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <Typography variant="subtitle2" fontWeight="medium">
              {t("companyInformation")}
            </Typography>
            <TextField
              autoFocus
              label={t("companyName")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.name}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("companyCode")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.company_code}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, company_code: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <Divider />
            <Typography variant="subtitle2" fontWeight="medium">
              {t("additionalInformation")}
            </Typography>
            <TextField
              label={t("description")}
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newCompanyData.description}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, description: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenAddModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleAddCompany}>
            {t("addCompany")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Invoice Email Scan Result Modal */}
      <Dialog open={openScanModal} onClose={() => setOpenScanModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t("invoiceEmailScanResult")}</DialogTitle>
        <DialogContent>
          {scanResult && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("foundMessages")}
                  </Typography>
                  <Typography variant="h6">{scanResult.found_messages}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("importedDocuments")}
                  </Typography>
                  <Typography variant="h6">{scanResult.imported_documents}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("skippedMessages")}
                  </Typography>
                  <Typography variant="h6">{scanResult.skipped_messages}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("alreadyProcessed")}
                  </Typography>
                  <Typography variant="h6">{scanResult.already_processed_messages}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("pendingSplitMessages")}
                  </Typography>
                  <Typography variant="h6">{scanResult.pending_split_messages || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    {t("pendingSplitJobs")}
                  </Typography>
                  <Typography variant="h6">{scanResult.pending_split_jobs || 0}</Typography>
                </Grid>
              </Grid>
              <Divider />
              <Box sx={{ maxHeight: 420, overflow: "auto" }}>
                {(scanResult.messages || []).map((message) => {
                  const documentsStatus = message.documents_status || {};
                  const documentsStatusText = `${documentsStatus.existing_count || 0}/${
                    documentsStatus.expected_count || message.imported_documents_count || 0
                  }`;
                  return (
                    <Accordion key={message.uid} disableGutters variant="outlined" sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                          flexWrap="wrap"
                          width="100%"
                        >
                          <Box minWidth={180}>
                            <Typography variant="body2" fontWeight="medium">
                              {message.message_date
                                ? new Date(message.message_date).toLocaleString()
                                : t("dateUnknown")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              UID {message.uid}
                            </Typography>
                          </Box>
                          <Box flex={1} minWidth={220}>
                            <Typography variant="body2">{message.sender || "-"}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {message.subject || "-"}
                            </Typography>
                          </Box>
                          <Chip size="small" label={emailStatusLabel(message.status)} />
                          <Chip
                            size="small"
                            color={documentsStatus.status === "present" ? "success" : "default"}
                            label={`${t("documents")}: ${documentsStatusText}`}
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                              {t("company")}
                            </Typography>
                            <Typography variant="body2">{message.company || "-"}</Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                              {t("processedAt")}
                            </Typography>
                            <Typography variant="body2">
                              {message.processed_at
                                ? new Date(message.processed_at).toLocaleString()
                                : "-"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t("attachments")}
                            </Typography>
                            {(message.attachment_names || []).length > 0 ? (
                              <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                                {message.attachment_names.map((name) => (
                                  <Chip key={`${message.uid}-${name}`} size="small" label={name} />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2">{t("notAvailableForOldLog")}</Typography>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t("documentCheck")}
                            </Typography>
                            <Typography variant="body2">
                              {documentStatusLabel(documentsStatus.status)}: {documentsStatusText}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              {t("recipients")}
                            </Typography>
                            <Typography variant="body2">{message.recipients || "-"}</Typography>
                          </Grid>
                        </Grid>
                        {message.error && (
                          <Typography variant="caption" color="error" display="block" mt={1}>
                            {message.error}
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="contained" color="info" onClick={() => setOpenScanModal(false)}>
            {t("close")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("areYouSure")} {selectedCompany?.name}? {t("thisActionCannotBeUndone")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenDeleteModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="error" onClick={handleDeleteCompany}>
            {t("delete")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default CompanyManagement;
